/**
 * Reports → Conversation Reports → Location Performance.
 *
 * Conversation volume and service quality broken up by one property of the
 * conversation — Location by default, any of them by choice. The page itself is
 * thin: `ReportShell` owns the toolbar, the Table ⇄ Analytics tabs, paging and
 * the empty states, so this file is the report's own three decisions — which
 * catalog, which table, which analytics.
 *
 * The column catalog is rebuilt whenever the grouped dimension changes, since
 * the first column IS that dimension.
 */
import { useMemo, useState, type JSX } from 'react'
import { Building2 } from 'lucide-react'

import LocationPerformanceFilters from '../../../components/reports/location-performance/LocationPerformanceFilters.js'
import LocationPerformanceAnalytics from '../../../components/reports/location-performance/LocationPerformanceAnalytics.js'
import {
  LOCATION_PERFORMANCE_REPORT_ID,
  buildLocationPerformanceColumns,
  sanitizeLocationColumns,
} from '../../../components/reports/location-performance/columns.js'
import ReportDataTable from '../../../components/reports/shared/ReportDataTable.js'
import ReportShell from '../../../components/reports/shared/ReportShell.js'
import {
  useLocationPerformanceReport,
  type LocationPerformanceFilters as Filters,
  type LocationPerformanceRow,
} from '../../../hooks/useLocationPerformanceReport.js'
import { useReportView } from '../../../hooks/useReportView.js'

export default function LocationPerformanceReportPage(): JSX.Element {
  const { rows, groupLabel, loading, error, hasRun, run, reset } = useLocationPerformanceReport()
  const [filtersOpen, setFiltersOpen] = useState(true)
  const [ranWith, setRanWith] = useState<Filters | null>(null)

  const catalog = useMemo(
    () => buildLocationPerformanceColumns(groupLabel),
    [groupLabel],
  )

  const { columns, setColumns, setColumnsLocal, commit } = useReportView(
    LOCATION_PERFORMANCE_REPORT_ID,
    catalog.map((c) => c.key),
    (saved) => sanitizeLocationColumns(saved, catalog),
  )

  const handleRun = (filters: Filters): void => {
    setRanWith(filters)
    setFiltersOpen(false)
    void run(filters)
  }

  const toggleColumn = (key: string): void => {
    if (catalog.find((c) => c.key === key)?.locked) return
    const order = catalog.map((c) => c.key)
    const next = columns.includes(key)
      ? columns.filter((k) => k !== key)
      : [...columns, key].sort((a, b) => order.indexOf(a) - order.indexOf(b))
    setColumns(next)
  }

  const reorder = (from: string, to: string): void => {
    if (from === to) return
    setColumnsLocal((prev) => {
      const next = [...prev]
      const fromIdx = next.indexOf(from)
      const toIdx = next.indexOf(to)
      if (fromIdx === -1 || toIdx === -1) return prev
      next.splice(toIdx, 0, ...next.splice(fromIdx, 1))
      return next
    })
  }

  const subtitle = ranWith
    ? `${rows.length} ${groupLabel.toLowerCase()}${rows.length === 1 ? '' : 's'}`
      + ` · ${ranWith.startDate} → ${ranWith.endDate} · ${ranWith.timezone}`
    : 'Conversation volume and service quality, broken up by any conversation property'

  return (
    <ReportShell<LocationPerformanceRow>
      Icon={Building2}
      title="Location Performance"
      subtitle={subtitle}
      hasRun={hasRun}
      ranWith={ranWith}
      loading={loading}
      error={error}
      rows={rows}
      catalog={catalog}
      visibleColumns={columns}
      onToggleColumn={toggleColumn}
      onReorderColumn={reorder}
      onCommitColumns={commit}
      searchPlaceholder={`Search ${groupLabel.toLowerCase()}…`}
      readCell={(row, key) => (row as unknown as Record<string, unknown>)[key]}
      reportId={LOCATION_PERFORMANCE_REPORT_ID}
      reportName="Location Performance"
      exportParams={ranWith && {
        timezone: ranWith.timezone,
        groupBy: ranWith.groupBy,
        startDate: ranWith.startDate,
        endDate: ranWith.endDate,
        locationIds: ranWith.locationIds.join(','),
        branchIds: ranWith.branchIds.join(','),
        departmentIds: ranWith.departmentIds.join(','),
      }}
      onOpenFilters={() => setFiltersOpen(true)}
      renderTable={(pageRows, visibleColumns) => (
        <ReportDataTable
          rows={pageRows as unknown as Record<string, unknown>[]}
          columns={catalog}
          visibleColumns={visibleColumns}
        />
      )}
      renderAnalytics={(visible) => (
        <LocationPerformanceAnalytics rows={visible} groupLabel={groupLabel} />
      )}
      emptyTitle="No report yet"
      emptyDescription="Choose a start date, an end date and what to group by in Filters, then run the report."
      noResultsTitle="No conversations in this window"
      noResultsDescription="Nothing arrived in the selected scope between those dates. Try widening the window, or clearing the location filters."
    >
      <LocationPerformanceFilters
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onRun={handleRun}
        onReset={() => { setRanWith(null); reset() }}
        running={loading}
      />
    </ReportShell>
  )
}
